import {
  types,
  type Instance,
  getParent,
  getRoot,
  flow,
} from 'mobx-state-tree';

import type { NotesQuery } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { PageConfig } from 'src/libs/PageConfig';
import { trackAddedNote, trackDeletedNote } from 'src/utils/events';

import createApplicationNote from '../../graphql/hirer/createApplicationNote';
import fetchApplicationNotes from '../../graphql/hirer/fetchApplicationNotes';
import removeApplicationNote from '../../graphql/hirer/removeApplicationNote';
import type { IStore } from '../Store';
import {
  STATE_ERROR,
  STATE_LOADED,
  stateType,
  ACTION_ERROR,
  ACTION_DONE,
} from '../state';

import type { IJobApplication } from './JobApplication';
import Note from './Note';

const Notes = types
  .model('Notes', {
    noteList: types.array(Note),
    noteCount: types.optional(types.number, 0),
    state: stateType,
  })
  .views((self) => ({
    get rootStore() {
      return getRoot(self) as IStore;
    },

    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },

    get trackingAttributes(): {
      candidateId: string;
      currentPage: typeof TRACKING_CANDIDATE_DETAILS;
    } {
      return {
        ...(this.jobApplication as IJobApplication).baseTrackingAttributes,
        currentPage: TRACKING_CANDIDATE_DETAILS,
      };
    },
  }))
  .actions((self) => {
    const loadNotes = flow(function* ({
      jobId,
      applicationCorrelationId,
      addCustomError,
    }: {
      jobId: string;
      applicationCorrelationId: string;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      try {
        const data: NotesQuery = yield fetchApplicationNotes({
          input: {
            jobId,
            applicationCorrelationId,
          },
        });

        if (
          data.applicationNotesV2.__typename ===
          'ApplicationNotesResponseSuccess'
        ) {
          self.noteList = data.applicationNotesV2.result.map((note) =>
            Note.create({
              id: note.id,
              content: note.content,
              createdUser: `${note.createdUserFirstname} ${note.createdUserLastname}`,
              createdDate: note.createdDate,
              createdUserId: Number(note.createdUserId),
            }),
          ) as any;
          self.noteCount = data.applicationNotesV2.result.length;
          self.state = STATE_LOADED;
        }

        if (data.applicationNotesV2.__typename === 'ResponseError') {
          throw new Error(data.applicationNotesV2.error);
        }
      } catch (error: any) {
        self.state = STATE_ERROR;
        addCustomError({
          error,
          context: {
            activity: 'LOAD_NOTES',
            description: 'Error attempting: queryApplicationNotes',
            jobApplicationId: self.jobApplication.id,
          },
        });
      }
    });

    const removeNote: (
      noteId: string,
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) => Promise<'done' | 'error'> = flow(function* (
      noteId: string,
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) {
      try {
        yield removeApplicationNote(
          (self.jobApplication as IJobApplication).id,
          noteId,
        );
        self.noteList = self.noteList.filter(
          (note) => note.id !== noteId,
        ) as any;
        self.noteCount = self.noteList.length;
        trackDeletedNote(self.trackingAttributes);
        return ACTION_DONE;
      } catch (error: any) {
        addCustomError({
          error,
          context: {
            errorCode: 'REMOVE_NOTE',
            description: 'Error attempting: removeApplicationNote',
            jobApplicationId: self.jobApplication.id,
            noteId,
          },
        });
        return ACTION_ERROR;
      }
    });

    const addNote: (
      content: string,
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) => Promise<'done' | 'error'> = flow(function* (
      content: string,
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) {
      try {
        const createdNote = yield createApplicationNote(
          (self.jobApplication as IJobApplication).id,
          content,
        );
        self.noteList.unshift(createdNote);
        self.noteCount = self.noteList.length;
        trackAddedNote(self.trackingAttributes);
        return ACTION_DONE;
      } catch (error: any) {
        addCustomError({
          error,
          context: {
            activity: 'CREATE_NOTE',
            description: 'Error attempting: createApplicationNote',
            jobApplicationId: self.jobApplication.id,
          },
        });
        return ACTION_ERROR;
      }
    });

    return { loadNotes, addNote, removeNote };
  });

export interface INotes extends Instance<typeof Notes> {}

export default Notes;
